import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Rocket, Save, RefreshCw } from 'lucide-react';
import { ThemeToggle } from '../../_shared/components';
import { PageHeader } from './PageHeader';
import { Button, Modal } from '../../_shared/components';
import { MemberTable } from './MemberTable';
import { StatusGroupConfig } from './StatusGroupConfig';
import { ColumnManager } from './ColumnManager';
import { WaterfallConfig } from './WaterfallConfig';
import { CopyEditor } from './CopyEditor';
import { TemplateSidebar } from './TemplateSidebar';
import { LaunchSidebar } from './LaunchSidebar';
import { SavedWaterfallAccordion } from './SavedWaterfallAccordion';
import { WaterfallPreview } from './WaterfallPreview';
import type { StatusMapping, ScorecardTemplate, CampaignMember, EnrichmentFlag } from './types';
import { templateToRows, evalCondition, type WaterfallRow, type CopyEditorTarget } from './waterfallTypes';
import { useLauncherCampaign } from '../hooks/useLauncherCampaign';
import { useLauncherMembers } from '../hooks/useLauncherMembers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { syncCampaignMembers } from '../api-client';
import { useLauncherStatuses } from '../hooks/useLauncherStatuses';
import { useLauncherTemplates } from '../hooks/useLauncherTemplates';
import { useLauncherWaterfall } from '../hooks/useLauncherWaterfall';
import { useEnrichmentFlags } from '../hooks/useEnrichmentFlags';
import { useDispatchLog } from '../hooks/useDispatchLog';
import { useReenrichDispatch } from '../hooks/useReenrichDispatch';
import { useSaveLauncherWaterfall } from '../hooks/useSaveLauncherWaterfall';
import { DispatchLogTable } from './DispatchLogTable';
import { DispatchDetailSidebar } from './DispatchDetailSidebar';
import { CampaignInfoPanel } from './CampaignInfoPanel';
import { CampaignDetailSidebar } from './CampaignDetailSidebar';
import { patchCampaignDetails } from '../event-manager-client';
import type { PatchCampaignFields } from '../event-manager-client';
import type { DispatchLogRow } from '../api-client';

// ── Helpers ──────────────────────────────────────────────────────────────────

function downloadExcludedCsv(members: CampaignMember[], flags: EnrichmentFlag[], campaignName: string) {
  const exclusionFlags = flags.filter(f => f.type === 'exclusion');
  const excluded = members.filter(m => exclusionFlags.some(f => evalCondition(m, f.condition)));
  const header = ['Name', 'Email', 'Title', 'Account', 'Sales Tier', 'CM Status', 'Exclusion Reasons'];
  const rows = excluded.map(m => {
    const reasons = exclusionFlags.filter(f => evalCondition(m, f.condition)).map(f => f.label).join('; ');
    return [
      m.contact_name ?? '',
      m.contact_email ?? '',
      m.contact_title ?? '',
      m.account_name ?? '',
      m.sales_tier ?? '',
      m.cm_status ?? '',
      reasons,
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
  });
  const csv = [header.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${campaignName.replace(/[^a-z0-9]/gi, '_')}_excluded_members.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildDefaultMapping(sfdcStatuses: string[], scorecardColumns: string[]): StatusMapping {
  const mapping: StatusMapping = {};
  for (const col of scorecardColumns) {
    const match = sfdcStatuses.find(s => s.toLowerCase() === col.toLowerCase());
    mapping[col] = match ? [match] : [];
  }
  return mapping;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function LauncherDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { campaign, isLoading, refetch: refetchCampaign } = useLauncherCampaign(id ?? '');
  const { members, isLoading: membersLoading } = useLauncherMembers(id ?? '');
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { isMarketingUser, isAdmin } = useCurrentUser();

  const syncMutation = useMutation({
    mutationFn: () => syncCampaignMembers(apiFetch, id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id ?? '', 'members'] });
    },
  });
  const isSyncing = syncMutation.isPending || membersLoading;

  // Trigger member sync when campaign loads if members_last_synced_at is null
  useEffect(() => {
    if (!campaign || !id || syncMutation.isPending) return;
    if (campaign.members_last_synced_at === null || campaign.members_last_synced_at === undefined) {
      syncMutation.mutate();
    }
  // Only run once when campaign first loads
  }, [campaign?.id]);
  const { statuses: sfdcStatuses } = useLauncherStatuses(id ?? '');
  const { templates } = useLauncherTemplates();
  const { waterfallRows: savedRows, savedStatusMapping, isLoading: waterfallLoading, isError: waterfallError, refetch: refetchWaterfall } = useLauncherWaterfall(id ?? '');
  const { flags: enrichmentFlags } = useEnrichmentFlags();
  const { rows: dispatchLogRows, isLoading: dispatchLogLoading } = useDispatchLog(id ?? '');

  const defaultColumns = useMemo(() => {
    const template = templates.find(t => t.campaign_type === campaign?.type);
    return template?.cm_status_columns ?? ['Attended', 'No-Show'];
  }, [templates, campaign?.type]);

  const initialRows = useMemo(() => {
    const template = templates.find(t => t.campaign_type === campaign?.type);
    if (!template) return [];
    return templateToRows(template.rows ?? [], defaultColumns);
  }, [templates, campaign?.type, defaultColumns]);

  const [statusColumns, setStatusColumns] = useState<string[]>([]);
  const [statusMapping, setStatusMapping] = useState<StatusMapping>({});
  const [waterfallRows, setWaterfallRows] = useState<WaterfallRow[]>([]);
  const [copyEditorTarget, setCopyEditorTarget] = useState<CopyEditorTarget | null>(null);

  // Template sidebar state
  const [templateSidebarOpen, setTemplateSidebarOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<ScorecardTemplate | null>(null);
  // key + override rows used to force-remount WaterfallConfig when a template is applied
  const [waterfallKey, setWaterfallKey] = useState(0);
  const [overrideInitialRows, setOverrideInitialRows] = useState<WaterfallRow[] | null>(null);

  const previewRows = useMemo(() =>
    previewTemplate ? templateToRows(previewTemplate.rows ?? [], statusColumns) : null,
    [previewTemplate, statusColumns],
  );

  // Launch sidebar state
  const [launchSidebarOpen, setLaunchSidebarOpen] = useState(false);

  // Test mode state
  const [testMode, setTestMode] = useState(false);

  // Dispatch detail sidebar state
  const [selectedDispatchRow, setSelectedDispatchRow] = useState<DispatchLogRow | null>(null);

  // Info tab state
  const [infoTab, setInfoTab] = useState<'members' | 'info'>('members');

  // Back-navigation warning modal
  const [showBackModal, setShowBackModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCampaignSidebar, setShowCampaignSidebar] = useState(false);
  const patchCampaignMutation = useMutation({
    mutationFn: (fields: PatchCampaignFields) => patchCampaignDetails(apiFetch, id!, fields),
    onSuccess: () => {
      setShowCampaignSidebar(false);
      void refetchCampaign();
    },
  });

  const reenrichMutation = useReenrichDispatch();

  // ── Auto-save (lifted from WaterfallConfig so closures see owned state) ──
  const saveMutation = useSaveLauncherWaterfall(id ?? '');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowsInitialized = useRef(false);

  // Seed columns + mapping once defaultColumns resolves from the API
  useEffect(() => {
    if (!defaultColumns.length || statusColumns.length > 0) return;
    // Wait for the waterfall query to finish before deciding saved vs default
    if (waterfallLoading) return;
    // A failed read must not look like "nothing saved": seeding the default mapping would
    // then be autosaved over the stored one.
    if (waterfallError) return;

    // If we have a saved mapping from the DB, restore columns from its keys
    if (savedStatusMapping && Object.keys(savedStatusMapping).length > 0) {
      setStatusColumns(Object.keys(savedStatusMapping));
      setStatusMapping(savedStatusMapping);
      return;
    }

    // Otherwise wait for SFDC statuses before building default mapping
    if (sfdcStatuses.length > 0) {
      setStatusColumns(defaultColumns);
      setStatusMapping(buildDefaultMapping(sfdcStatuses, defaultColumns));
    }
  }, [defaultColumns.join(','), sfdcStatuses.join(','), savedStatusMapping, waterfallLoading, waterfallError]);

  // Auto-save every 15s after changes
  useEffect(() => {
    if (!rowsInitialized.current) { rowsInitialized.current = true; return; }
    if (campaign?.launcher_status === 'launched') return;
    if (waterfallError) return;
    if (Object.keys(statusMapping).length === 0) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveMutation.mutate({ rows: waterfallRows, statusMapping }, {
        onSuccess: () => setSavedAt(new Date()),
      });
    }, 15_000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [waterfallRows, statusMapping, waterfallError]);

  const handleSaveNow = () => {
    if (waterfallError) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    saveMutation.mutate({ rows: waterfallRows, statusMapping }, {
      onSuccess: () => setSavedAt(new Date()),
    });
  };

  const handleColumnsChange = (cols: string[]) => {
    setStatusColumns(cols);
    setStatusMapping(prev => {
      const next: StatusMapping = {};
      for (const col of cols) {
        next[col] = prev[col] ?? [];
      }
      return next;
    });
  };

  const handleTemplateApply = (t: ScorecardTemplate) => {
    setOverrideInitialRows(templateToRows(t.rows ?? [], statusColumns));
    setWaterfallKey(k => k + 1);
    setPreviewTemplate(null);
    setTemplateSidebarOpen(false);
  };

  const openTemplateSidebar = () => {
    setCopyEditorTarget(null);
    setLaunchSidebarOpen(false);
    setSelectedDispatchRow(null);
    setTemplateSidebarOpen(true);
  };

  const openLaunchSidebar = () => {
    setCopyEditorTarget(null);
    setTemplateSidebarOpen(false);
    setSelectedDispatchRow(null);
    setLaunchSidebarOpen(true);
  };

  const handleBack = () => {
    if (isLaunched) { navigate('/launcher'); return; }
    setShowBackModal(true);
  };


  if (isLoading || waterfallLoading) {
    return (
      <main className="flex-1 flex items-center justify-center bg-nightshade-50 dark:bg-nightshade-950">
        <div className="w-6 h-6 rounded-full border-2 border-blueberry-400 border-t-transparent animate-spin" />
      </main>
    );
  }

  if (waterfallError) {
    return (
      <main className="flex-1 flex flex-col items-center justify-center gap-3 bg-nightshade-50 dark:bg-nightshade-950">
        <p className="text-nightshade-600 dark:text-nightshade-300">
          Terra could not read this campaign&apos;s saved waterfall. Nothing has been changed. Saving is off until it loads.
        </p>
        <Button variant="ghost" size="sm" onClick={() => { void refetchWaterfall(); }}>Retry</Button>
      </main>
    );
  }

  if (!campaign) {
    return (
      <main className="flex-1 flex items-center justify-center bg-nightshade-50 dark:bg-nightshade-950">
        <p className="text-nightshade-400">Campaign not found.</p>
      </main>
    );
  }

  const isLaunched = campaign.launcher_status === 'launched';

  return (
    <div className="h-full flex overflow-hidden bg-nightshade-50 dark:bg-nightshade-950">
      {/* Content area + copy editor sidebar as flex siblings so the sidebar pushes content */}
      <div className="flex-1 flex min-w-0 overflow-hidden">
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="px-6 py-6">
          {/* ── Page header with back, title, meta, tabs ── */}
          <PageHeader
            campaignId={id!}
            campaignName={campaign.name}
            subtype={campaign.subtype}
            status={campaign.status}
            launcherStatus={campaign.launcher_status}
            eventDate={campaign.start_date ?? campaign.event_date}
            city={campaign.city ?? null}
            currentTab="launcher"
            onBackClick={isLaunched ? undefined : handleBack}
            detailsOpen={showDetails}
            onToggleDetails={() => setShowDetails(v => !v)}
            onEditDetails={() => setShowCampaignSidebar(true)}
          />
          {showDetails && (
            <div className="mt-2 mb-4">
              <CampaignInfoPanel campaign={campaign} onRefresh={() => { void refetchCampaign(); }} />
            </div>
          )}
        </div>
        <div className="max-w-[1080px] mx-auto px-6 pb-6 space-y-6">
          {/* Action bar */}
          <div className="flex items-center justify-end gap-2">
              {!isLaunched && (
                <Button variant="ghost" size="sm" onClick={handleSaveNow} className="flex items-center gap-1.5">
                  <Save className="w-3.5 h-3.5" />
                  Save
                </Button>
              )}
              <ThemeToggle />
              <Button
                variant={isLaunched ? 'ghost' : 'primary'}
                size="sm"
                className="flex items-center gap-1.5"
                onClick={openLaunchSidebar}
              >
                <Rocket className="w-4 h-4" />
                {isLaunched ? 'Launched' : 'Preview Launch'}
              </Button>
          </div>

          {/* ── Column manager + status mapping ── */}
          <div className="space-y-4">
            <ColumnManager columns={statusColumns} onChange={handleColumnsChange} disabled={isLaunched} />
            <StatusGroupConfig
              sfdcStatuses={sfdcStatuses}
              scorecardColumns={statusColumns}
              mapping={statusMapping}
              onChange={setStatusMapping}
              disabled={isLaunched}
            />
          </div>

          {/* ── Campaign members / info tabs ── */}
          <section>
            <div className="flex items-center gap-1 mb-3">
              <button
                onClick={() => setInfoTab('members')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  infoTab === 'members'
                    ? 'bg-blueberry-50 dark:bg-blueberry-900/30 text-blueberry-600 dark:text-blueberry-400'
                    : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
                }`}
              >
                Campaign members
                <span className={`ml-1.5 ${infoTab === 'members' ? 'text-blueberry-400 dark:text-blueberry-500' : 'text-nightshade-400'}`}>
                  {members.length.toLocaleString()}
                </span>
              </button>
              <button
                onClick={() => setInfoTab('info')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  infoTab === 'info'
                    ? 'bg-blueberry-50 dark:bg-blueberry-900/30 text-blueberry-600 dark:text-blueberry-400'
                    : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
                }`}
              >
                Campaign info
              </button>
            </div>

            {infoTab === 'members' ? (
              <MemberTable
                members={members}
                waterfallRows={waterfallRows}
                enrichmentFlags={enrichmentFlags}
                isSyncing={isSyncing}
                onHandleExcluded={(() => {
                  const exclusionFlags = enrichmentFlags.filter(f => f.type === 'exclusion');
                  const excludedMembers = members.filter(m => exclusionFlags.some(f => evalCondition(m, f.condition)));
                  if (exclusionFlags.length === 0 || excludedMembers.length === 0) return undefined;
                  return {
                    onDownloadCsv: () => downloadExcludedCsv(members, enrichmentFlags, campaign.name),
                    onReenrich: () => reenrichMutation.mutate({ campaignId: id ?? '', memberIds: excludedMembers.map(m => m.id) }),
                    reenrichState: reenrichMutation.isPending ? 'pending' : reenrichMutation.isSuccess ? 'success' : reenrichMutation.isError ? 'error' : 'idle',
                  };
                })()}
              />
            ) : (
              <CampaignInfoPanel campaign={campaign} onRefresh={() => { void refetchCampaign(); }} />
            )}
          </section>

          {/* ── Waterfall configuration ── */}
          <section className="space-y-3">
            {/* Saved configuration accordion */}
            {savedRows && savedRows.length > 0 && (
              <SavedWaterfallAccordion rows={savedRows} members={members} />
            )}

            {/* Template preview — shown while template sidebar is open with a selection */}
            {previewRows && previewTemplate && (
              <WaterfallPreview
                rows={previewRows}
                members={members}
                templateName={previewTemplate.name}
              />
            )}

            {!isLaunched && (
              <div className="flex justify-end">
                <div className="flex rounded-lg overflow-hidden border border-nightshade-200 dark:border-nightshade-700 text-xs">
                  <button
                    onClick={() => setTestMode(false)}
                    className={`px-3 py-1.5 transition-colors ${!testMode ? 'bg-blueberry-500 text-white font-medium' : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'}`}
                  >Live</button>
                  <button
                    onClick={() => setTestMode(true)}
                    className={`px-3 py-1.5 border-l border-nightshade-200 dark:border-nightshade-700 transition-colors ${testMode ? 'bg-blueberry-500 text-white font-medium' : 'text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'}`}
                  >Test</button>
                </div>
              </div>
            )}
            <WaterfallConfig
              key={waterfallKey}
              campaignType={campaign.type}
              statusColumns={statusColumns}
              statusMapping={statusMapping}
              members={members}
              campaign={campaign}
              onRowsChange={setWaterfallRows}
              initialRows={overrideInitialRows ?? initialRows}
              onEditCopy={target => { setSelectedDispatchRow(null); setCopyEditorTarget(target); }}
              onOpenTemplateSidebar={openTemplateSidebar}
              campaignId={id ?? ''}
              disabled={isLaunched}
              isTestMode={testMode}
              savedAt={savedAt}
              savePending={saveMutation.isPending}
              saveError={saveMutation.isError}
              onSaveNow={async () => {
                if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
                return new Promise<void>((resolve, reject) => {
                  saveMutation.mutate({ rows: waterfallRows, statusMapping }, {
                    onSuccess: () => { setSavedAt(new Date()); resolve(); },
                    onError: () => reject(),
                  });
                });
              }}
            />
          </section>

          {/* ── Dispatch log ── */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100">Dispatch log</h2>
            <DispatchLogTable
              rows={dispatchLogRows}
              isLoading={dispatchLogLoading}
              selectedRowId={selectedDispatchRow?.id}
              onSelectRow={row => {
                setCopyEditorTarget(null);
                setTemplateSidebarOpen(false);
                setSelectedDispatchRow(prev => prev?.id === row.id ? null : row);
              }}
            />
          </section>

        </div>
      </main>

      {/* Copy editor — in-flow, pushes <main> left as it opens */}
      {copyEditorTarget && (
        <CopyEditor
          key={`${copyEditorTarget.bucketName}-${copyEditorTarget.statusColumn}`}
          {...copyEditorTarget}
          onClose={() => setCopyEditorTarget(null)}
        />
      )}

      {/* Template sidebar — in-flow, pushes <main> left as it opens */}
      {templateSidebarOpen && (
        <TemplateSidebar
          templates={templates}
          onPreview={t => setPreviewTemplate(t)}
          onApply={handleTemplateApply}
          onClose={() => { setPreviewTemplate(null); setTemplateSidebarOpen(false); }}
        />
      )}

      {/* Dispatch detail sidebar — in-flow, pushes <main> left */}
      {selectedDispatchRow && (
        <DispatchDetailSidebar
          key={selectedDispatchRow.id}
          row={selectedDispatchRow}
          onClose={() => setSelectedDispatchRow(null)}
        />
      )}
      </div>

      {/* Launch sidebar — fixed overlay */}
      {launchSidebarOpen && (
        <LaunchSidebar
          campaign={campaign}
          waterfallRows={waterfallRows}
          members={members}
          enrichmentFlags={enrichmentFlags}
          statusMapping={statusMapping}
          isLaunched={isLaunched}
          dispatchLogRows={dispatchLogRows}
          onClose={() => setLaunchSidebarOpen(false)}
          onLaunched={() => setLaunchSidebarOpen(false)}
        />
      )}

      {/* Back-navigation save modal */}
      <Modal open={showBackModal} onClose={() => setShowBackModal(false)} hideCloseButton>
        <div className="max-w-sm">
          <h3 className="text-base font-semibold text-content-primary mb-2">
            Save before leaving?
          </h3>
          <p className="text-sm text-content-secondary mb-5">
            Your changes will be saved before navigating back.
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => { setShowBackModal(false); navigate('/launcher'); }}
              className="text-sm text-content-tertiary hover:text-content-primary transition-colors"
            >
              Discard
            </button>
            <Button
              size="sm"
              onClick={() => {
                if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
                saveMutation.mutate({ rows: waterfallRows, statusMapping }, {
                  onSuccess: () => { setShowBackModal(false); navigate('/launcher'); },
                });
              }}
              loading={saveMutation.isPending}
            >
              Save & go back
            </Button>
          </div>
        </div>
      </Modal>

      {showCampaignSidebar && campaign && (
        <CampaignDetailSidebar
          campaign={campaign}
          onClose={() => setShowCampaignSidebar(false)}
          onSave={async (fields) => { await patchCampaignMutation.mutateAsync(fields); }}
          saving={patchCampaignMutation.isPending}
          readOnly={!(isMarketingUser || isAdmin)}
        />
      )}

    </div>
  );
}
